import { Button } from "@progress/kendo-react-buttons";
import { Window, WindowActionsBar } from "@progress/kendo-react-dialogs";
import { Input, TextArea, type InputChangeEvent, type InputHandle, type TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import { useEffect, useRef, useState, type FC, type SubmitEvent } from "react";
import useCurrentPlan from "../../../../hooks/useCurrentPlan";

interface SaveScenarioWindowProps {
    onClose: () => void;
    onSave: (name: string, description: string) => void;
    scenarioDescription?: string;
}

const SaveScenarioWindow: FC<SaveScenarioWindowProps> = ({ onClose, onSave, scenarioDescription }) => {
    const { scenarioDefaultName } = useCurrentPlan();
    const [name, setName] = useState<string>(scenarioDefaultName ?? "");
    const [description, setDescription] = useState<string>(scenarioDescription ?? "");
    const inputRef = useRef<InputHandle>(null);
    
    useEffect((): (() => void) => {
        const timeoutId = window.setTimeout((): void => {
            inputRef.current?.element?.focus();
        }, 100);

        return (): void => {
            window.clearTimeout(timeoutId);
        };
    }, []);

    const handleNameChange = (event: InputChangeEvent): void => {
        setName(event.target.value as string);
    };

    const handleDescriptionChange = (event: TextAreaChangeEvent): void => {
        setDescription(event.target.value as string);
    };

    const handleSave = (): void => {
        const trimmedName = name.trim();
        const trimmedDesc = description.trim();

        if (!trimmedName) {
            return;
        }

        onSave(trimmedName, trimmedDesc);
    };

    const handleSubmit = (event: SubmitEvent): void => {
        event.preventDefault();
        handleSave();
    };

    return (
        <Window
            title="Save Scenario"
            initialWidth={480}
            initialHeight={340}
            modal={true}
            onClose={onClose}
        >
            <form onSubmit={handleSubmit}>
                <div className="d-flex flex-column gap-2">
                    <label htmlFor="scenario-name" className="form-label">
                        Name
                    </label>

                    <Input
                        ref={inputRef}
                        id="scenario-name"
                        value={name}
                        onChange={handleNameChange}
                    />

                </div>

                <div className="d-flex flex-column gap-2">
                    <label htmlFor="scenario-description" className="form-label">
                        Description
                    </label>

                    <TextArea
                        id="scenario-description"
                        value={description}
                        rows={2}
                        onChange={handleDescriptionChange}
                    />
                </div>

                <button type="submit" hidden />
            </form>

            <WindowActionsBar>
                <Button type="button" onClick={onClose}>
                    Cancel
                </Button>

                <Button
                    type="submit"
                    themeColor="primary"
                    disabled={!name.trim()}
                    onClick={handleSave}
                >
                    Save
                </Button>
            </WindowActionsBar>
        </Window>
    );
};

export default SaveScenarioWindow;
